import { PageState } from "src/app/core/base-page";
import { Entity } from "src/app/core/data/schema/entity";
import { CheckoutCartItem, CheckoutSpecial } from "./cart-item";
import { ProductType } from "./product";
import { FulfillmentType } from "../community";
import { SurveyResponse } from "../surveys/survey-response";


export class Transaction extends Entity {

    public readonly PLATFORM_PAYPAL = "paypal";
    public readonly PLATFORM_STRIPE = "stripe";
    public readonly PLATFORM_SQUARE = "square";


    static className = 'Transaction';
    static idField = 'auctionPaymentId';

    amount: number;
    auctionId: String;
    auctionPaymentId: number;
    communityId: number;
    currency: string;
    fee1: number;
    fee2: number;
    firstName: string;
    fulfillmentType: FulfillmentType;
    lastName: string;
    deferred: boolean;
    modifiedByEmail: string;
    name: string;
    partnerFeePaid: boolean;
    platform: string;
    purchases: TransactionDetail[] = [];
    rawRequest: string;
    status: TransactionStatus;
    declineReason: string;
    surcharge: number;
    tax1: number;
    tax2: number;
    tax3: number;
    timestamp: string;
    transactionToken: string;
    userEmail: string;
    userId: number;
    isFulfilled: boolean;
    shippingAddress: string;
    billingAddress: string;
    fee1Label: string;
    fee2Label: string;
    tax1Label: string;
    tax2Label: string;
    tax3Label: string;
    organization: string;
    phone: string;

    // mapped
    createdByName: string;
    hasSurveyResponses: boolean;

    // for display purposes
    fulfillmentTypeString;
    statusString: string;
    subtotal: number;



    constructor(init?: Partial<Transaction>) {
        super('Transaction', null, 'auctionpayments');
        Object.assign(this, init);
        this.initialize();
    }

    get paidFor() {
        return this.status === TransactionStatus.Approved || this.status === TransactionStatus.Refunded;
    }

    public static async doCheckout(checkoutRequest: CheckoutRequest) {
        return new PaymentCheckout(await Entity.factory.post('Preparing', checkoutRequest as any, 'auctionpayments', `checkout`)); // return the transaction token or the checkout url
    }

    public static async doTestPayment() {
        return await Entity.factory.post('Preparing', {} as any, 'auctionpayments', `testpayments`, 'text'); // return the transaction token or the checkout url
    }

    public static async completePaypalTestPayment(token: string) {
        return await Entity.factory.post('Preparing', new PaymentCheckout({ transactionToken: token }) as any, 'auctionpayments', `testpayments`, 'text'); // return the transaction token or the checkout url
    }

    public static async completeCheckout(paymentCheckout: PaymentCheckout) {
        return await Entity.factory.post('Completing', paymentCheckout, 'auctionpayments', `checkout/complete`);
    }

    public static async cancelOrder(id: number) {
        return await Entity.factory.post('Cancelling', null, 'auctionpayments', `cancel/${id}`);
    }

    public static async fetchForCommunity(hideLoad: boolean = false) {
        return await Entity.factory.fetchObj(Transaction, 'auctionpayments', `community`, 'json', hideLoad);
    }

    public static async fetchForUser(userId: number) {
        return await Entity.factory.fetchObj(Transaction, 'auctionpayments', `user/${userId}`);
    }

    public static async load(id: number, silent: boolean = false) {
        return await this.getByIdNoCache(Transaction, id, 'auctionpayments', null, silent);
    }

    public override async save(busyState?: PageState, controller?: string, endpoint?: string): Promise<boolean> {
        const didSave = await super.save(busyState, controller, endpoint);
        if (didSave) {
            this.initialize();
        }
        return didSave;
    }

    async fulfill() {
        Object.assign(this, await Entity.factory.post('Fulfill', null, 'auctionpayments', `fulfill/${this.auctionPaymentId}`) as Entity);
        this.initialize();
        return this;
    }

    async refund(detination: RefundDestination, reason?: string) {
        const request = new RefundRequest({
            transactionId: this.auctionPaymentId,
            refundAmount: this.amount,
            refundDestination: detination,
            refundReason: reason
        });

        Object.assign(this, await Entity.factory.post("Refund", request as any, 'auctionpayments', `refund`));
        this.initialize();
        return this;
    }

    async cancel() {
        Object.assign(this, await Entity.factory.post("Cancel", null, 'auctionpayments', `cancel/${this.auctionPaymentId}`));
        this.initialize();
        return this;
    }

    async fetchSurveyResponses() {
        if (this.hasSurveyResponses) {
            return await Entity.factory.fetchObj(SurveyResponse, 'surveyresponses', `forpurchase/${this.auctionPaymentId}`);
        }
        return [];
    }

    private initialize() {
        this.purchases = this.purchases.map(p => new TransactionDetail(p));
        this.currency = this.currency.toUpperCase();

        // for display purposes determine the fulfillment type string
        switch (this.fulfillmentType) {
            case FulfillmentType.Delivery:
                this.fulfillmentTypeString = 'Delivery';
                break;
            case FulfillmentType.Pickup:
                this.fulfillmentTypeString = 'Pickup';
                break;
            default:
                this.fulfillmentTypeString = 'No Fulfillment';
                break;
        }

        // for display purposes determine the status string
        if (this.status === TransactionStatus.Failed) {
            this.statusString = 'Cancelled';
        } else if (this.status === TransactionStatus.Refunded) {
            this.statusString = 'Refunded';
        } else if (this.status === TransactionStatus.Expired) {
            this.statusString = 'Expired';
        } else if (this.status === TransactionStatus.Declined) {
            this.statusString = 'Declined';
        } else if (this.status === TransactionStatus.Cancelled) {
            this.statusString = 'Cancelled';
        } else if (this.status !== TransactionStatus.Approved) { // if not paid, cancelled, or refunded, then it's unpaid
            this.statusString = 'Unpaid';
        } else if (this.status === TransactionStatus.Approved) {
            this.statusString = (this.isFulfilled) ? 'Completed' : 'Open';
        }

        // for display purposes determine the subtotal
        this.subtotal = this.amount - this.fee1 - this.fee2 - this.tax1 - this.tax2 - this.tax3 - this.surcharge;

        this.formatPhoneNumber();
    }

    private formatPhoneNumber() {
        // format the phone number
        if (this.phone) {
            const phone = this.phone.replace(/\D/g, '');
            if (phone.length === 10) {
                this.phone = `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6, 10)}`;
            } else if (phone.length === 7) {
                this.phone = `${phone.substring(0, 3)}-${phone.substring(3, 7)}`;
            }
        }
    }

}


export class TransactionDetail {
    costPerUnit: number;
    quantity: number;
    status: number;
    notes: string;
    name: string;
    productId: number;
    productType: ProductType;
    purchaseGroup: number;
    identifier: string;
    paidPerUnit: number;
    detailType: TransactionDetailType;
    specialId: number;
    requiresFulfillment: boolean;
    fulfillmentType: FulfillmentType;

    constructor(init?: Partial<TransactionDetail>) {
        Object.assign(this, init);
    }
}

export class CheckoutRequest {

    static className = 'Checkout';

    cart: CheckoutCartItem[];
    specials: CheckoutSpecial[];
    eventTickets: CheckoutCartItem[];
    userId?: number;
    transactionToken?: string;
    paymentMethod?: string; // for wallet or offline payments
    transactionId?: number; // used when admin is completing an open order
    fulfillmentType: FulfillmentType;
    shippingAddress: string;
    firstName: string;
    lastName: string;
    storeShippingAddress: boolean;
    email: string;
    phoneNumber: string;

    // fundraising campaign id
    fcId?: number;

    constructor(init?: Partial<CheckoutRequest>) {
        Object.assign(this, init);
    }
}

export class RefundRequest {
    transactionId: number;
    refundAmount: number;
    refundReason?: string;
    refundDestination: RefundDestination = RefundDestination.OriginalPaymentMethod;

    constructor(init?: Partial<RefundRequest>) {
        Object.assign(this, init);
    }
}

export enum RefundDestination {
    EWallet = 1,
    OriginalPaymentMethod = 2,
}

export class PaymentCheckout extends Entity {

    checkoutUrl = '';
    platform = '';
    transactionId = 0;
    transactionToken = '';
    currency = 'USD';
    checkoutTotal = 0;
    checkoutDescription = '';

    constructor(init?: Partial<PaymentCheckout>) {
        super('Payment', null, 'auctionpayments');
        Object.assign(this, init);
    }

}

export enum TransactionDetailType {
    Product = 0,
    Special = 1,
    WalletTopUp = 2,
    AuctionEventTicket = 3,
    Voucher = 4,
    Donation = 5,
    EventTicket = 6
}

export enum TransactionStatus {
    Pending = 1,
    Approved = 2,
    Declined = 3,
    Refunded = 4,
    Expired = 5,
    Failed = 6,
    Cancelled = 7
}